import { useEffect, useState } from 'react';

type TabInfo = {
  id: number; // Incremental ID
  url: string;
};

export const useTabManager = () => {
  const [tabId, setTabId] = useState<number | null>(null);
  const [activeTabs, setActiveTabs] = useState<TabInfo[]>([]);

  useEffect(() => {
    // Retrieve sessionStorage and localStorage data
    const storedTabId = sessionStorage.getItem('tabId');
    const storedTabs: TabInfo[] = JSON.parse(
      localStorage.getItem('activeTabs') || '[]'
    );

    let currentTabId: number;

    if (storedTabId) {
      // If a tabId already exists in sessionStorage, reuse it
      currentTabId = parseInt(storedTabId, 10);
    } else {
      // Generate the next available ID
      const nextTabId =
        storedTabs.length > 0
          ? Math.max(...storedTabs.map((tab) => tab.id)) + 1
          : 1;
      currentTabId = nextTabId;

      // Save this tabId in sessionStorage
      sessionStorage.setItem('tabId', currentTabId.toString());
    }

    setTabId(currentTabId);

    const currentTab: TabInfo = {
      id: currentTabId,
      url: window.location.href,
    };

    // Add this tab to active tabs only if it doesn't already exist
    const isTabAlreadyStored = storedTabs.some(
      (tab) => tab.id === currentTabId
    );
    const updatedTabs = isTabAlreadyStored
      ? storedTabs
      : [...storedTabs, currentTab];

    localStorage.setItem('activeTabs', JSON.stringify(updatedTabs));
    setActiveTabs(updatedTabs);

    // Cleanup on tab close
    const handleBeforeUnload = () => {
      const storedTabs = JSON.parse(localStorage.getItem('activeTabs') || '[]');
      const remainingTabs = storedTabs.filter(
        (tab: TabInfo) => tab.id !== currentTabId
      );
      localStorage.setItem('activeTabs', JSON.stringify(remainingTabs));
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      const storedTabs = JSON.parse(localStorage.getItem('activeTabs') || '[]');
      const remainingTabs = storedTabs.filter(
        (tab: TabInfo) => tab.id !== currentTabId
      );
      localStorage.setItem('activeTabs', JSON.stringify(remainingTabs));
    };
  }, []);

  useEffect(() => {
    // Listen for changes in active tabs from other tabs
    const handleStorageChange = () => {
      const storedTabs = JSON.parse(localStorage.getItem('activeTabs') || '[]');
      setActiveTabs(storedTabs);
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return { tabId, activeTabs };
};
